import React from "react";

const allSkills = [
  "AI/ML", "Java", "Python", "Django", "React", "JavaScript", "TypeScript",
  "Node.js", "Spring Boot", "Golang", "AWS", "Azure", "GCP", "Docker",
  "Kubernetes", "Terraform", "Ansible", "Jenkins", "GitHub", "GitLab",
  "MongoDB", "MySQL", "PostgreSQL", "CI/CD",
];

const About = () => {
  return (
    <div
      name="about"
      className="w-full bg-gradient-to-b from-zinc-900 via-slate-950 to-gray-950 text-white py-6 md:py-8 lg:py-10"
    >
      <div className="max-w-screen-lg mx-auto px-6 md:px-8">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-1 w-12 bg-blue-500"></div>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight">
            About Me
          </h3>
        </div>

        {/* About Me Description */}
        <div className="max-w-3xl space-y-4 mb-6">
          <p className="text-gray-200 text-lg md:text-xl lg:text-2xl leading-relaxed font-light tracking-wide">
            Senior Software Engineer with <span className="text-white font-semibold">6+ years</span> building cloud-native systems and developer platforms across backend, DevOps, and AI/ML-adjacent work.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-900/50 border border-gray-700/40 rounded-lg p-4 backdrop-blur-sm">
              <h4 className="text-sm md:text-base font-semibold text-blue-300 uppercase tracking-wide mb-2">
                What I deliver
              </h4>
              <ul className="space-y-1.5 text-base md:text-lg text-gray-200 font-light">
                <li>• Reliable services and APIs (microservices, automation).</li>
                <li>• CI/CD + IaC that speeds delivery and reduces risk.</li>
                <li>• Observability and operational excellence in production.</li>
              </ul>
            </div>

            <div className="bg-gray-900/50 border border-gray-700/40 rounded-lg p-4 backdrop-blur-sm">
              <h4 className="text-sm md:text-base font-semibold text-cyan-300 uppercase tracking-wide mb-2">
                Domains
              </h4>
              <ul className="space-y-1.5 text-base md:text-lg text-gray-200 font-light">
                <li>• Cloud platforms (AWS-focused), Kubernetes.</li>
                <li>• Security-minded engineering (least privilege, scanning).</li>
                <li>• Data/ETL workflows and platform tooling.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Summary · Recent Roles · Key Skills */}
        <div className="grid gap-4 md:gap-6 md:grid-cols-3">
          {/* Quick Summary */}
          <div className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Quick Summary
            </h3>
            <ul className="space-y-1.5 text-[11px] sm:text-xs md:text-sm text-gray-300 leading-relaxed">
              <li>• 6+ years building cloud-native, production-grade systems.</li>
              <li>• Blend of backend, DevOps, and platform engineering.</li>
              <li>• Focus on reliability, scalability, and delivery speed.</li>
            </ul>
          </div>

          {/* Recent Roles */}
          <div className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Recent Roles
            </h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">Software Engineer (AI/ML)</p>
                <p className="text-[11px] sm:text-xs text-gray-400">JPMorgan Chase & Co. (JPMC) · 04-2026 – Present · Columbus, OH</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">Sr. AWS DevOps / Developer</p>
                <p className="text-[11px] sm:text-xs text-gray-400">NetJets · 02-2026 – 04-2026 · Columbus, OH</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">Software / DevOps Engineer</p>
                <p className="text-[11px] sm:text-xs text-gray-400">Upwork · 04-2024 – 06-2025 · Columbus, OH</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">Software / DevOps Engineer</p>
                <p className="text-[11px] sm:text-xs text-gray-400">JPMorgan Chase & Co. (JPMC) · 04-2024 – 06-2025 · Columbus, OH</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">DevOps Engineer</p>
                <p className="text-[11px] sm:text-xs text-gray-400">Wipro · 04-2024 – 07-2024 · Remote</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">Sr. Software Engineer</p>
                <p className="text-[11px] sm:text-xs text-gray-400">Olive AI · 03-2020 – 04-2023 · Columbus, OH</p>
              </div>
            </div>
          </div>

          {/* Key Skills */}
          <div className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Key Skills
            </h3>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {allSkills.map((skill) => (
                <span
                  key={skill}
                  className="px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full bg-gradient-to-r from-blue-600/20 to-cyan-500/20 border border-blue-500/40 text-blue-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
